/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import axios from "axios";
import config from "../config/profileCard.config.json";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_MY_ORGANIZATION, UPDATE_MY_ORGANIZATION } from "../graphql";
import { VApplication } from "../../../application";

import { AppNavigatorService } from "../../appNavigatorService";
import { GET_MY_PROFILE } from "../graphql/getMyProfile.query";

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.AppNavigatorService") as AppNavigatorService;
};

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IProfileCardMethods extends IPluginMethodMap {
  getProfileCardProps(args: any): Promise<any>;
}

export class ProfileCardService implements IAgentPlugin {
  readonly methods: IProfileCardMethods;

  constructor() {
    this.methods = {
      getProfileCardProps: this.getProps.bind(this),
    };
  }

  async getProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const { getCode } = require("iso-3166-1-alpha-2");
    try {
      if (client) {
        let _resprofile = await client.query({
          query: GET_MY_PROFILE,
          fetchPolicy: "network-only",
        });
        const res = await client.query({
          query: GET_MY_ORGANIZATION,
          fetchPolicy: "network-only",
        });
        if (res?.data) {
          console.log(`profile data`, res.data);
          let country = res?.data?.getMyOrgProfile?.address?.country;
          const isISO2CountryCode = (code: any) => {
            var pattern = /^[A-Za-z]{2}$/;
            return pattern.test(code);
          };
          const territories = [
            "American Samoa",
            "Anguilla",
            "Bermuda",
            "British Virgin Islands",
            "Cayman Islands",
            "Cook Islands",
            "Falkland Islands",
            "Faroe Islands",
            "Gibraltar",
            "Greenland",
            "Jersey",
            "Montserrat",
            "Niue",
            "Norfolk Island",
            "Northern Mariana Islands",
            "Saint Barthélemy",
            "Saint Helena",
            "Saint Martin",
            "Saint Pierre and Miquelon",
            "Sint Maarten",
            "Tokelau",
            "Turks and Caicos Islands",
            "U.S. Virgin Islands",
            "Wallis and Futuna",
          ];
          if (
            _resprofile?.data?.getMyProfile?.role[0]?.name === "admin" ||
            _resprofile?.data?.getMyProfile?.role[0]?.name === "superAdmin"
          ) {
            if (
              !isISO2CountryCode(getCode(country)) &&
              !territories.includes(country)
            ) {
              const update = await client.mutate({
                mutation: UPDATE_MY_ORGANIZATION,
                variables: {
                  input: {
                    address: {
                      country: null,
                    },
                  },
                },
                fetchPolicy: "no-cache",
              });
            }
          }

          const _res = await client.query({
            query: GET_MY_ORGANIZATION,
            fetchPolicy: "network-only",
          });
          const _data: any = await this.getFormattedProfile(
            _res.data?.getMyOrgProfile,
          );
          return {
            ...(_data || {}),
            tweetyProps: this.formatFormProps(
              config.TWEETY_PROPS,
              _res.data?.getMyOrgProfile,
            ),
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
    // return {...(config || {})}
  }

  private async getFormattedProfile(data: any) {
    console.log("data orgprofile==>");
    let _profileInfo: any = {
      title: null,
      subtitle: null,
      image: data?.logo ? data?.logo : "",
      description: data?.bio,
      listItems: [
        {
          key: "email",
          title: "Email Address",
          subTitle: data?.email,
          icon: "https://img.icons8.com/fluency/96/mail.png",
        },
        {
          key: "phoneNumber",
          title: "Contact Number",
          subTitle: data?.primaryContactNo,
          icon: "https://img.icons8.com/fluency/96/phone.png",
        },
        {
          key: "address1",
          title: "Address Line 1",
          subTitle: data?.address?.addressLine1,
          icon: "https://img.icons8.com/fluency/96/map-marker.png",
        },
        {
          key: "address2",
          title: "Address Line 2",
          subTitle: data?.address?.addressLine2,
          icon: "https://img.icons8.com/fluency/96/map-marker.png",
        },
        {
          key: "address3",
          title: "Address Line 3",
          subTitle: data?.address?.addressLine3,
          icon: "https://img.icons8.com/fluency/96/map-marker.png",
        },
        {
          key: "city",
          title: "City",
          subTitle: data?.address?.city,
          icon: "https://img.icons8.com/fluency/96/building-with-rooftop-terrace.png",
        },
        {
          key: "state",
          title: "State",
          subTitle: data?.address?.state,
          icon: "https://img.icons8.com/fluency/96/region-code.png",
        },
        {
          key: "country",
          title: "Country",
          subTitle: data?.address?.country,
          icon: "https://img.icons8.com/fluency/96/geography.png",
        },
        {
          key: "zipcode",
          title: "Zip Code",
          subTitle: data?.address?.zipcode,
          icon: "https://img.icons8.com/fluency/96/map-marker.png",
        },
        {
          key: "website",
          title: "Website",
          subTitle: data?.website,
          icon: "https://img.icons8.com/fluency/96/geography.png",
        },
      ],
      buttons: [
        {
          key: "edit",
          title: "Edit",
          color: "secondary",
          isPresent: false,
        },
      ],
      initialData: {
        name: data?.name,
        email: data?.email,
        description: data?.description,
        avatar: data?.logo,
        phoneNumber: data?.primaryContactNo,
        address1: data?.address?.addressLine1,
        address2: data?.address?.addressLine2,
        address3: data?.address?.addressLine3,
        city: data?.address?.city,
        state: data?.address?.state,
        country: data?.address?.country,
        zipcode: data?.address?.zipcode,
        website: data?.website,
      },
    };
    if (data?.name) {
      _profileInfo.title = data?.name;
    }
    if (data?.description) {
      _profileInfo.subtitle = data?.description;
    }
    return _profileInfo;
  }

  private formatFormProps(formProps: any, data: any) {
    let _res: any = formProps;
    console.log("res data==>", data);
    _res.initialData = {
      name: data?.name,
      email: data?.email,
      description: data?.description,
      avatar: data?.logo,
      phoneNumber: data?.primaryContactNo,
      address1: data?.address?.addressLine1,
      address2: data?.address?.addressLine2,
      address3: data?.address?.addressLine3,
      city: data?.address?.city,
      state: data?.address?.state,
      country: data?.address?.country,
      zipcode: data?.address?.zipcode,
      website: data?.website,
    };

    return _res;
  }

  private async isEditEnabled() {
    const srv = getWhoamiSrv();
    const user = await srv?.whoAmI();
    console.log(`user in profile service`, user);
    let formatted = true;
    if (user?.whoami === "admin") {
      formatted = false;
    }
    return formatted;
  }
}
